package co.laiautomation.downtyme

import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.value.MutableValue
import com.arkivanov.decompose.value.Value
import com.laiautomation.kdxp.KdxpConfig
import com.laiautomation.kdxp.RootComponentDefault
import com.laiautomation.kdxp.appmodel.KdxpTargetPlatform
import com.laiautomation.kdxp.sites.LiferaySite
import kotlin.coroutines.cancellation.CancellationException

/** Runtime state for Downtyme's application-owned Liferay Object schema. */
sealed interface DowntymeSchemaStartupState {
    data object WaitingForCampaign : DowntymeSchemaStartupState

    data class Installing(
        val siteId: Long,
        val siteName: String,
    ) : DowntymeSchemaStartupState

    data class Ready(
        val siteId: Long,
        val bindings: DowntymeSchemaBindings,
    ) : DowntymeSchemaStartupState

    data class Failed(
        val siteId: Long,
        val siteName: String,
        val message: String,
    ) : DowntymeSchemaStartupState
}

/**
 * Downtyme's KDXP root component.
 *
 * KDXP owns campaign/site and application snapshot provisioning. Downtyme
 * installs its application-owned Object definitions whenever an authorized
 * administrator opens a deployment where they are missing. Ordinary users
 * only bind site-scoped repositories and never administer Object definitions.
 */
class DowntymeRootComponent(
    componentContext: ComponentContext,
    targetPlatform: KdxpTargetPlatform,
    config: KdxpConfig = downtymeConfig(),
) : RootComponentDefault(
    componentContext = componentContext,
    targetPlatform = targetPlatform,
    config = config,
) {
    private val mutableSchemaStartupState =
        MutableValue<DowntymeSchemaStartupState>(DowntymeSchemaStartupState.WaitingForCampaign)

    val schemaStartupState: Value<DowntymeSchemaStartupState> = mutableSchemaStartupState

    private var schemaProvisioned = false
    private var schemaBindings: DowntymeSchemaBindings? = null
    private var boundSiteId: Long? = null

    /** Typed repositories for the currently selected campaign. */
    var campaignRepositories: DowntymeCampaignRepositories? = null
        private set

    /**
     * Ensures Downtyme's application-owned Object definitions exist.
     *
     * The check runs only for users KDXP identifies as setup administrators.
     * It supports both a fresh KDXP setup and a KDXP platform that was already
     * initialized before Downtyme was first launched.
     */
    suspend fun provisionDowntymeSchema(
        site: LiferaySite,
        force: Boolean = false,
    ) {
        if (!sessionState.value.authenticated) return
        if (!force && schemaProvisioned) {
            schemaBindings?.let { bindings ->
                mutableSchemaStartupState.value = DowntymeSchemaStartupState.Ready(
                    siteId = site.id,
                    bindings = bindings,
                )
            }
            return
        }

        mutableSchemaStartupState.value = DowntymeSchemaStartupState.Installing(
            siteId = site.id,
            siteName = site.displayName,
        )

        try {
            val provisioner = DowntymeSchemaProvisioner(repositories.objects)
            val existingBindings = if (force) {
                null
            } else {
                try {
                    provisioner.resolveBindings()
                } catch (error: CancellationException) {
                    throw error
                } catch (_: Exception) {
                    null
                }
            }
            val installedNow = existingBindings == null
            val bindings = existingBindings ?: provisioner.provision()

            schemaProvisioned = true
            schemaBindings = bindings
            mutableSchemaStartupState.value = DowntymeSchemaStartupState.Ready(
                siteId = site.id,
                bindings = bindings,
            )
            println("Downtyme schema ready for ${site.displayName} (${site.id}).")

            if (installedNow) {
                // Reload after Liferay publishes the generated /o/c endpoints.
                session.clearSelectedSite()
                selectSite(site)
            }
        } catch (error: CancellationException) {
            throw error
        } catch (error: Exception) {
            val message = error.message ?: "Downtyme schema initialization failed."
            mutableSchemaStartupState.value = DowntymeSchemaStartupState.Failed(
                siteId = site.id,
                siteName = site.displayName,
                message = message,
            )
            println("Downtyme schema initialization failed: $message")
        }
    }

    /**
     * Binds typed repositories to the selected campaign without inspecting or
     * modifying Object definitions.
     */
    fun bindDowntymeCampaign(site: LiferaySite) {
        if (!sessionState.value.authenticated) return
        if (boundSiteId == site.id && campaignRepositories != null) return

        val bindings = DowntymeSchemaBindings.runtimeDefaults()
        campaignRepositories = DowntymeCampaignRepositories(
            objects = repositories.objects,
            scopeKey = site.id.toString(),
            bindings = bindings,
        )
        boundSiteId = site.id
        mutableSchemaStartupState.value = DowntymeSchemaStartupState.Ready(
            siteId = site.id,
            bindings = bindings,
        )
        println("Downtyme repositories bound to campaign ${site.displayName} (${site.id}).")
    }
}
